import { useMutation, useQueryClient } from "@tanstack/react-query";
import { AlertTriangle, Pause } from "lucide-react";

import { Modal } from "../../components/Modal/Modal";
import { Button } from "../../components/Button/Button";
import { Alert } from "../../components/Alert/Alert";
import { useToast } from "../../components/Toast/toastContext";
import { ApiError, describeError } from "../../lib/errors";
import { suspendProject } from "./api";
import { projectQueryKey } from "./useProject";
import styles from "./SuspendProjectModal.module.css";

interface Props {
  open: boolean;
  onClose: () => void;
  /** The project to suspend — the id drives the POST, the name the title. */
  project: { id: string; name: string };
}

/**
 * The question the Suspend action grew when suspension started to mean
 * something.
 *
 * While `games.status` was an administrative label the game API never read,
 * firing the mutation straight off the menu item cost nothing to get wrong.
 * Now one press closes every live socket of a production project and turns
 * sign-in away for as long as it stands, so the press has to be a decision
 * rather than a slip of the pointer.
 *
 * Shaped on DeleteProjectModal and deliberately missing its one distinctive
 * part, the type-the-project-name field. Suspension is undone by the menu item
 * that replaces this one, so the worst a mis-click costs is the seconds between
 * the two — and a field demanded for that as well is a field an operator learns
 * to fill in without reading the sentence above it, which is precisely what it
 * is worth on the dialog that really cannot be undone.
 *
 * The request lives here rather than at the two call sites for the same reason
 * it lives in RenameProjectModal: the overview page and the projects list had a
 * mutation each, with toasts that matched and invalidation sets that did not,
 * and neither page has anything to do after a suspend that the other does not.
 */
export function SuspendProjectModal({ open, onClose, project }: Props) {
  const qc = useQueryClient();
  const { push } = useToast();

  const mut = useMutation({
    mutationFn: () => suspendProject(project.id),
    onSuccess: async () => {
      push({ variant: "success", message: "Project suspended." });
      // A prefix, so the activity feed and the analytics cached under it go
      // with the project itself — the suspend writes an entry into that feed
      // and the badge beside it has just changed.
      await qc.invalidateQueries({ queryKey: projectQueryKey(project.id) });
      await qc.invalidateQueries({ queryKey: ["projects"] });
      onClose();
    },
    // The dialog stays open on a failure: nothing was suspended, the toast
    // says why, and the button that failed is still under the pointer.
    onError: (err) => {
      push({
        variant: "danger",
        message:
          err instanceof ApiError
            ? describeError(err.code)
            : describeError("internal"),
      });
    },
  });

  return (
    <Modal
      open={open}
      onClose={onClose}
      // Nothing to press twice while the POST is in flight, and the × says so
      // rather than going quiet.
      closeDisabled={mut.isPending}
      title={project.name ? `Suspend ${project.name}?` : "Suspend project?"}
      icon={<AlertTriangle size={17} aria-hidden="true" />}
      iconTone="danger"
      footer={
        <>
          <Button
            variant="secondary"
            onClick={onClose}
            disabled={mut.isPending}
          >
            Cancel
          </Button>
          <Button
            variant="danger"
            onClick={() => mut.mutate()}
            loading={mut.isPending}
            icon={<Pause size={14} aria-hidden="true" />}
          >
            Suspend project
          </Button>
        </>
      }
    >
      <Alert variant="warn" title="Players lose access straight away.">
        Every live connection is closed, sign-in and the game API answer a
        refusal for every player, and the bot simulation stops.
      </Alert>

      {/* Warn and not danger, and the difference is the whole point of the
          sentence below: the red alerts in this panel are the ones that end
          with "this cannot be undone", and this one ends with the opposite. */}
      {/* <b>Unsuspend</b> and not "Unsuspend project": the two menus this
          dialog opens from spell the item differently — "Unsuspend project" on
          the project page, plain "Unsuspend" in the projects list — and the
          shorter one is the prefix of both. Naming the longer form would send
          an operator who suspended from the list looking for a label that is
          not there. */}
      <p className={styles.note}>
        Nothing is deleted or changed: data, settings and leaderboards stay
        exactly as they are, and leaderboard cycles keep rolling. One click on{" "}
        <b>Unsuspend</b> puts the game back.
      </p>
    </Modal>
  );
}
